/**
 * Org Digests: shared fact shapes persisted on `org_digests.facts_json` and
 * consumed by the API, web, email, and chat formatters.
 *
 * IDs inside {@link DigestFacts} are UUIDs (decision 12 in the org-digests
 * spec) — renderers encode them with `@chatsdet/ids`, and route params are
 * decoded with `extractUuid()` at the boundary. Repos never see prefixed IDs.
 *
 * @module digests
 * @see specs/org-digests.md
 */

import type { IssueSeverity } from './issue-core.js';

export type DigestKind = 'daily' | 'weekly';

/** Cap on the inline lists (new Issues, promoted flows) so a bulk day cannot bloat `facts_json`, the LLM prompt, or the email. */
export const DIGEST_LIST_CAP = 10;

/** Calendar days in the trend chart: the window-end day and the six before it. */
export const DIGEST_SERIES_DAYS = 7;

export interface DigestIssueRef {
  issueId: string;
  title: string;
  severity: IssueSeverity;
  filedBy: 'agent' | 'manual';
}

export interface DigestFlowRef {
  workflowId: string;
  name: string;
}

/** A flow whose definition Cofactor's maintenance agent changed, with the agent's customer-facing summary. */
export interface DigestMaintainedRef {
  workflowId: string;
  name: string;
  changeSummary: string;
}

/**
 * A flow automatic maintenance has stopped on because of something the customer
 * must fix. Only customer-owned reasons reach here — the API never puts a
 * Cofactor-owned reason into the facts.
 */
export interface DigestNeedsAttentionRef {
  workflowId: string;
  name: string;
  /** Short plain label for why maintenance stopped, e.g. "The app appears broken". */
  reasonLabel: string;
  /** What the customer should do; null only on a run recorded without one. */
  remediation: string | null;
}

/** One calendar day of the trend chart. Rates are 0–100; null when there is nothing to measure. */
export interface DigestSeriesPoint {
  /** YYYY-MM-DD in `window.timezone`. */
  date: string;
  /** Runs with a verdict that day (passed or failed; skipped/degraded/canceled/unfinished excluded). */
  runs: number;
  /** Passed / runs that day; null when runs is 0. */
  passRate: number | null;
  /** Product-map linkage % from the latest snapshot on or before that day; null when none exists. */
  coverage: number | null;
}

/**
 * One product area (or a property's Unassigned bucket when `areaId` is null),
 * in map order. Both rates read the coverage snapshot as of the window-end
 * day (same rule as the series) and are the product map page's two per-area
 * chips: `coverage` is "% of capabilities covered" and `passRate` is "% of
 * mapped capabilities passing". `isNew` and
 * `capabilitiesAdded` span the same trailing {@link DIGEST_SERIES_DAYS} days
 * as the headline coverage delta, so a drop can be attributed to new surface
 * rather than read as a regression.
 */
export interface DigestAreaPoint {
  areaId: string | null;
  /** Area title; "Unassigned" for the null bucket. */
  title: string;
  /** Active capabilities in the area (snapshot `capabilitiesTotal`) — the size behind `coverage`. */
  capabilities: number;
  /** Capabilities with ≥1 active link to an eligible workflow (snapshot `capabilitiesLinked`) — the numerator of `coverage`. */
  mapped: number;
  /** Mapped capabilities currently passing (snapshot `capabilitiesCovered`) — the numerator of `passRate`. */
  passing: number;
  /** % of `capabilities` mapped; null when the area has no capabilities or no snapshot yet. */
  coverage: number | null;
  /** % of mapped capabilities currently passing (`passing` / `mapped`); null when nothing is mapped. */
  passRate: number | null;
  /** The area itself was created within the series span. */
  isNew: boolean;
  /** Active capabilities created in the area within the series span. */
  capabilitiesAdded: number;
}

/**
 * The product map's size and coverage at the window end, and what changed
 * since the window start — the Capabilities and Coverage panels. `total` and
 * `mapped` are the sums of {@link DigestFacts.areas} (same snapshot as the
 * series), so they match the product map page.
 */
export interface DigestCapabilityFacts {
  /** Active capabilities as of the window-end day. */
  total: number;
  /** Capabilities with an active link to an eligible workflow — the numerator of coverage. */
  mapped: number;
  /** The same two counts as of the calendar day the window opened on (latest snapshot on or before it); null when none exists. */
  previous: { total: number; mapped: number } | null;
  /** Active capabilities created inside the window; null on rows generated before this was recorded. */
  added: number | null;
  /** Active capabilities that existed before the window and were edited inside it; null on rows generated before this was recorded. */
  updated: number | null;
}

export interface DigestFacts {
  /** `timezone` is the IANA zone used for the series buckets and for displaying window dates ('UTC' when the org has no config row). */
  window: { start: string; end: string; timezone: string };
  /** Runs with a verdict in the window — the activity signal quiet detection uses. */
  runs: number;
  /** Workflows promoted (status → approved) in the window; `promoted` is exact, `items` is capped at {@link DIGEST_LIST_CAP}. */
  flows: { promoted: number; items: DigestFlowRef[] };
  /** Issues filed in the window (`filed` exact, `items` capped by severity) and the org's open Issues right now. */
  issues: { filed: number; items: DigestIssueRef[]; open: number };
  /** Flows the maintenance agent changed in the window (`workflow.maintained` events — the same rows the activity ledger shows); `count` exact, `items` capped at {@link DIGEST_LIST_CAP}. */
  maintained: { count: number; items: DigestMaintainedRef[] };
  /** Flows durably stuck on a customer-owned blocker right now (not windowed); `count` is exact up to 50 stuck flows checked (newest first), `items` capped at {@link DIGEST_LIST_CAP}. */
  needsAttention: { count: number; items: DigestNeedsAttentionRef[] };
  /** Suite runs that finished in the window, with their per-suite flow totals summed (the ledger's "X/Y passed" figures). */
  suites: { completed: number; flowsPassed: number; flowsTotal: number };
  /** The trailing {@link DIGEST_SERIES_DAYS} calendar days ending on the window-end day. */
  series: DigestSeriesPoint[];
  /** Per-area coverage and pass rate, in product-map order; empty when the org has no product map. */
  areas: DigestAreaPoint[];
  /** Product-map size and coverage now versus before the window. */
  capabilities: DigestCapabilityFacts;
}

/** One rendered line of a digest section; `url` present when the line links to an entity. */
export interface DigestSectionLine {
  text: string;
  url?: string;
}

/**
 * Flat text section for the chat formatters (Slack, Teams) — the two surfaces
 * that cannot render charts or badges. The email template and the web render
 * straight from {@link DigestFacts}.
 */
export interface DigestSection {
  title: string;
  lines: DigestSectionLine[];
  /**
   * Items the capped `lines` leave out (`count - items.length`); 0 when every
   * item is listed. Each renderer emits exactly one "…and N more" line from
   * this, folding in any lines it drops for its own size limit, so a message
   * never carries two disagreeing "more" lines.
   */
  more: number;
}

/**
 * One row of the chat formatters' product-map table (Slack `table` block,
 * Teams Adaptive Card `Table`), presentation-neutral. `area` is the title
 * plus any change note ("Billing · new area"); `bar` is the stacked health bar
 * from {@link formatDigestAreaBar}; `status` is the count label from
 * {@link formatDigestAreaStatus} ("5/8 mapped · 2 failing").
 */
export interface DigestAreaRow {
  area: string;
  bar: string;
  status: string;
}

/**
 * The digest's headline numbers, derived from {@link DigestFacts} — serialized
 * on `GET /digests` list rows (which omit `factsJson`) so the dashboard card
 * can show a stat row, and reused by the digest page's own strip.
 */
export interface DigestHeadline {
  flowsPromoted: number;
  issuesFiled: number;
  openIssues: number;
  suites: DigestFacts['suites'];
  /** Coverage % at the end of the series; null when the org has no snapshot. */
  coverage: number | null;
  /** Coverage growth over the series in percentage points; null when either end has no snapshot. */
  coverageDelta: number | null;
  /** Capabilities added to the product map over the series span — the denominator growth behind `coverageDelta`. */
  capabilitiesAdded: number;
}

/**
 * Legacy digest rows can predate later persisted fact keys. Normalize those
 * deploy-window rows at read/render time so every surface still treats the
 * schema contract as required for newly generated rows.
 */
export function normalizeDigestFacts(facts: DigestFacts): DigestFacts {
  const areas = Array.isArray(facts.areas) ? facts.areas.map(normalizeDigestArea) : [];
  return {
    ...facts,
    maintained: facts.maintained ?? { count: 0, items: [] },
    needsAttention: facts.needsAttention ?? { count: 0, items: [] },
    suites: facts.suites ?? { completed: 0, flowsPassed: 0, flowsTotal: 0 },
    areas,
    capabilities: facts.capabilities ?? {
      total: areas.reduce((sum, area) => sum + area.capabilities, 0),
      mapped: areas.reduce((sum, area) => sum + area.mapped, 0),
      previous: null,
      added: null,
      updated: null,
    },
  };
}

/**
 * Rows persisted before 2026-09-21 carry only the two rates; recover the
 * counts the health bar needs from them (exact when the rate was exact).
 */
function normalizeDigestArea(area: DigestAreaPoint): DigestAreaPoint {
  if (typeof area.mapped === 'number' && typeof area.passing === 'number') return area;
  const mapped = Math.round((area.capabilities * (area.coverage ?? 0)) / 100);
  const passing = Math.round((mapped * (area.passRate ?? 0)) / 100);
  return { ...area, mapped, passing };
}

export function digestHeadline(facts: DigestFacts): DigestHeadline {
  const normalized = normalizeDigestFacts(facts);
  const first = normalized.series[0]?.coverage ?? null;
  const last = normalized.series[normalized.series.length - 1]?.coverage ?? null;
  return {
    flowsPromoted: normalized.flows.promoted,
    issuesFiled: normalized.issues.filed,
    openIssues: normalized.issues.open,
    suites: normalized.suites,
    coverage: last,
    coverageDelta: first === null || last === null ? null : last - first,
    capabilitiesAdded: normalized.areas.reduce((sum, area) => sum + area.capabilitiesAdded, 0),
  };
}

/**
 * Digest number/date formatting, shared by the API's chat section builder,
 * the email template, and the web digest page — one source of formatting
 * semantics. Pure and dependency-free.
 */

/** "Suite runs: 3 · 40/42 Workflows passed" — the chat renderers' numbers line. */
export function formatSuiteRuns(suites: DigestFacts['suites']): string {
  return `Suite runs: ${suites.completed} · ${formatSuiteFlows(suites)}`;
}

/** "40/42 Workflows passed" — the KPI tile detail on email and web, and the tail of {@link formatSuiteRuns}. */
export function formatSuiteFlows(suites: DigestFacts['suites']): string {
  return `${suites.flowsPassed}/${suites.flowsTotal} Workflows passed`;
}

/** Whole-number percentage when exact, otherwise one decimal. `null` renders "—". */
export function formatDigestRate(rate: number | null): string {
  if (rate === null) return '—';
  return `${formatDigestMagnitude(rate)}%`;
}

/** Signed percentage-point delta, e.g. "+3.2 pts" / "−1.0 pts". `null` has no rendering — callers omit it. */
export function formatDigestDelta(delta: number | null): string | null {
  if (delta === null) return null;
  const sign = delta < 0 ? '−' : '+';
  return `${sign}${formatDigestMagnitude(Math.abs(delta))} pts`;
}

const DIGEST_BAR_WIDTH = 10;

/** The three states of a product-area health bar, one glyph per segment. */
export const DIGEST_BAR_PASSING = '🟩';
export const DIGEST_BAR_FAILING = '🟥';
export const DIGEST_BAR_UNMAPPED = '⬜';
export type DigestBarGlyph = typeof DIGEST_BAR_PASSING | typeof DIGEST_BAR_FAILING | typeof DIGEST_BAR_UNMAPPED;

type DigestAreaCounts = Pick<DigestAreaPoint, 'capabilities' | 'mapped' | 'passing'>;

/**
 * Stacked health bar for chat surfaces: 🟩 passing · 🟥 mapped but failing ·
 * ⬜ unmapped, ten segments of `capabilities`. An area
 * with no capabilities is an empty (all-unmapped) track. Segments round, so a
 * single failure among many capabilities may not earn a red segment — the
 * status label carries the exact counts.
 */
export function formatDigestAreaBar(area: DigestAreaCounts): string {
  // `passing ≤ mapped ≤ capabilities` by construction (snapshot columns), so
  // the rounded segment counts nest the same way.
  const segments = (count: number) =>
    area.capabilities === 0 ? 0 : Math.round((count / area.capabilities) * DIGEST_BAR_WIDTH);
  const mapped = segments(area.mapped);
  const passing = segments(area.passing);
  return (
    DIGEST_BAR_PASSING.repeat(passing) +
    DIGEST_BAR_FAILING.repeat(mapped - passing) +
    DIGEST_BAR_UNMAPPED.repeat(DIGEST_BAR_WIDTH - mapped)
  );
}

/** "5/8 mapped · 2 failing" / "4/8 mapped · all passing" / "not mapped yet" / "no capabilities". */
export function formatDigestAreaStatus(area: DigestAreaCounts): string {
  if (area.capabilities === 0) return 'no capabilities';
  if (area.mapped === 0) return 'not mapped yet';
  const failing = area.mapped - area.passing;
  return `${area.mapped}/${area.capabilities} mapped · ${failing === 0 ? 'all passing' : `${failing} failing`}`;
}

/**
 * Worst first: areas with failing capabilities (most failing first), then
 * mapped areas that all pass (least covered first), then unmapped areas in
 * map order — unmapped surface is backlog, not a regression — and empty
 * areas last.
 */
export function digestAreaOrder(a: DigestAreaCounts, b: DigestAreaCounts): number {
  const failing = (area: DigestAreaCounts) => area.mapped - area.passing;
  const rank = (area: DigestAreaCounts) =>
    failing(area) > 0 ? 0 : area.mapped > 0 ? 1 : area.capabilities > 0 ? 2 : 3;
  const coverage = (area: DigestAreaCounts) => (area.capabilities === 0 ? 0 : area.mapped / area.capabilities);
  if (rank(a) !== rank(b)) return rank(a) - rank(b);
  if (rank(a) === 0) return failing(b) - failing(a);
  if (rank(a) === 1) return coverage(a) - coverage(b);
  return 0;
}

function formatDigestMagnitude(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** Calendar date (YYYY-MM-DD) of an ISO timestamp in `timeZone` — the same zone the series is bucketed in. */
export function digestCalendarDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/**
 * Axis label for a series calendar date (YYYY-MM-DD): "Mon 8/31". Carries the
 * day of month because a seven-day series always repeats one weekday.
 */
export function digestDayLabel(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(
    day
  );
  return `${weekday} ${day.getUTCMonth() + 1}/${day.getUTCDate()}`;
}

/**
 * The {@link DIGEST_SERIES_DAYS} calendar days (YYYY-MM-DD, ascending) in
 * `timeZone` ending on the day that contains the instant just before `end`
 * (windows are half-open, so `end` itself belongs to the next day).
 */
export function digestSeriesDays(end: string, timeZone: string): string[] {
  const lastDay = digestCalendarDate(new Date(new Date(end).getTime() - 1).toISOString(), timeZone);
  const cursor = new Date(`${lastDay}T00:00:00Z`);
  const days: string[] = [];
  for (let i = 0; i < DIGEST_SERIES_DAYS; i += 1) {
    days.unshift(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return days;
}
